
import {
  defineComponent,
  ref,
  onMounted,
  toRef,
  nextTick,
  watch,
  computed,
} from "vue";
import {
  IDataTableFilterLookupOption,
  IDataTableFilterOption,
  IDataTableFilterOptionOptions,
} from "@/interfaces/iPageModel";
import Multiselect from "@vueform/multiselect";
import { MenuComponent } from "@/assets/ts/components";
import { getLookupFilters } from "@/services/api/Lookup.service";
import { SavedFilterAPI } from "@/services/api/SavedFilter.service";
import { useStore } from "vuex";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
import { ISavedFilter } from "@/interfaces/iSavedFilter";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import * as Yup from "yup";
import { TranslationService } from "@/core/services/TranslationService";
import AddEditModal from "@/components/modals/forms/AddEditModal.vue";
import { useToast } from "vue-toastification";
import VZShowIcon from "@/components/content/VZShowIcon.vue";
import VZHideIcon from "@/components/content/VZHideIcon.vue";
import { removeModalBackdrop } from "@/core/helpers/dom";
import VZLookup from "@/components/vz-lookup/VZLookup.vue";
export default defineComponent({
  name: "vz-filter",
  emits: ["filter-change", "default-filter"],
  components: {
    Multiselect,
    "vz-add-edit-modal": AddEditModal,
    VZShowIcon,
    VZHideIcon,
    VZLookup,
  },
  props: {
    options: {
      type: Array as () => Array<IDataTableFilterOption> | undefined,
      required: false,
    },
    defaultFilter: { type: Object },
    viewName: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    menuPlacement: { type: String, default: "bottom-end" },
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const triggerControl = ref<HTMLElement>();
    const store = useStore();
    const toast = useToast();
    const showAddEditModal = ref<boolean>(false);
    const defaultFilter = toRef(props, "defaultFilter");
    const filterOptions = toRef(props, "options");
    const defaultFilterSaved = ref<any>();
    const viewName = toRef(props, "viewName");
    const models = ref<{ [key: string]: any }>({});
    const appliedModels = ref<{ [key: string]: any }>({});
    const activeFilter = ref<number>(0);
    const lookupOptions = ref<IDataTableFilterOptionOptions[]>([]);
    const items = ref<Array<IDataTableFilterOption>>([]);
    const savedFilterList = ref<Array<ISavedFilter>>([]);
    const searchableFilterList = ref<Array<ISavedFilter>>([]);
    const showSavedFilters = ref<boolean>(true);
    const selectedPreSavedFilter = ref<number>(-1);
    const searchTerm = ref<string>("");
    const disableButton = ref<boolean>(false);
    const loading = toRef(props, "loading");
    const scrollRef = ref<HTMLElement | null>(null);
    const rowClasses = ref<string>(
      "menu menu-sub menu-sub-dropdown w-sm-400px w-md-800px"
    );
    const messages = {
      deleted: t("common.savedFilter.messages.deleted"),
      added: t("common.savedFilter.messages.added"),
      updated: t("common.savedFilter.messages.updated"),
    };

    const formData = ref<IAddEditFormModel>({
      header: " Pre Saved Filters",
      label: [{ addHeader: t("common.addPreSavedFilter") }],
      edit: false,
      data: null,
      validationSchema: Yup.object({
        title: Yup.string().required(
          t("common.filterName") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "title",
          label: t("common.filterName"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
      ],
    });
    const handleSelect = async (item) => {
      const index = models.value[item.key].indexOf("-1");
      if (index !== -1) {
        models.value[item.key].splice(index, 1);
        const values = item.options.slice(1).map((item) => item.value);
        models.value[item.key] = values;
      }
      await item.onChange(models.value[item.key]);
    };
    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    const displayDate = computed(() => {
      return process.env.VUE_APP_DISPLAY_DATE || "MM-DD-YYYY";
    });
    const displayDateTime = computed(() => {
      return process.env.VUE_APP_DISPLAY_TIME || "MM-DD-YYYY HH:mm";
    });
    const setActiveFilter = () => {
      activeFilter.value =
        Object.keys(models.value).filter((item) => {
          if (
            (Array.isArray(models.value[item]) &&
              !models.value[item]?.length) ||
            !models.value[item] ||
            models.value[item] === null
          ) {
            return false;
          }
          return true;
        }).length ?? 0;
    };
    const getLookupData = async () => {
      // store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
      try {
        if (filterOptions?.value && filterOptions?.value.length > 0) {
          items.value = [...filterOptions.value];
          const allOption = { label: "Select All", value: "-1" };
          const lookups = filterOptions.value?.filter(
            (item) => item.type === "lookup"
          ) as IDataTableFilterLookupOption[];

          if (lookups && lookups?.length > 0 && filterOptions.value) {
            const values = await Promise.all(
              lookups.map(async (item: IDataTableFilterLookupOption) => {
                const { url, textKey, valueKey } = item;
                return await getLookupFilters(url, textKey, valueKey);
              })
            );
            lookups.forEach((item, index) => {
              // store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
              const indexOf = items.value.findIndex(
                (innerItem) => innerItem.key === item.key
              );
              if (indexOf > -1) {
                if (item.key === "subType") {
                  const transformedOptions = values[index].map((option) => ({
                    ...option,
                    label: capitalizeLabel(option.label),
                  }));
                  items.value[indexOf].options = [
                    allOption,
                    ...transformedOptions,
                  ];
                } else {
                  items.value[indexOf].options = [allOption, ...values[index]];
                }
              }
            });
          }
        }
      } catch (e) {
        //
      }
    };
    function capitalizeLabel(label) {
      // Convert label to lowercase, split by underscore, capitalize each word, and join with space
      return label
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    const hasNonStatusFilters = (obj: any) => {
      if (!obj || typeof obj !== "object") return false;
      return Object.keys(obj).some((key) => {
        if (key === "status") return false;
        const val = obj[key];
        if (val === null || val === undefined) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === "string") return val.trim() !== "";
        if (typeof val === "object") return Object.keys(val).length > 0;
        return Boolean(val);
      });
    };
    const getAppliedFilters = async () => {
      await getSavedFilterList();
      models.value = { ...defaultFilterSaved.value };
      appliedModels.value = { ...models.value };
      setActiveFilter();
      emit("filter-change", models.value, false, false);
    };
    onMounted(async () => {
      nextTick(() => {
        const menuElement = document.querySelector(
          `#vz-sort-data-${viewName.value}`
        ) as HTMLElement;
        if (menuElement && !MenuComponent?.getInstance(menuElement)) {
          MenuComponent.reinitialization();
        }
      });
      await getLookupData();
      await getSavedFilterList();
      models.value = { ...defaultFilterSaved.value };
      appliedModels.value = { ...models.value };
      setActiveFilter();
      emit("filter-change", models.value, false, false);

      // Handling filters of sub tables
      var drawerElement = document.getElementById("vz__drawer");
      drawerElement?.addEventListener("transitionend", function (event) {
        if (event?.propertyName === "transform") {
          const targetElement = event.target as HTMLElement;
          if (targetElement.className === "drawer drawer-end drawer-on") {
            if (
              viewName.value === "eventManagementForm" ||
              viewName.value === "lookupsData" ||
              viewName.value === "eventConfigs" ||
              viewName.value === "usersActivity" ||
              viewName.value === "devicesActivity" ||
              viewName.value === "userRole" ||
              viewName.value === "formSite" ||
              viewName.value === "formUsersTable" ||
              viewName.value === "formEvent" ||
              viewName.value === "formsActivity" ||
              viewName.value === "userSites" ||
              viewName.value === "siteForms" ||
              viewName.value === "siteActivity" ||
              viewName.value === "siteDevice"
            ) {
              getSavedFilterList();
            }
          }
        }
      });
    });

    const updateFilter = (isUserApply = false, isReset = false) => {
      if (isUserApply && !hasNonStatusFilters(models.value)) {
        isReset = true;
      }
      setActiveFilter();
      if (viewName.value === "ViewRole") {
        closeToggle();
      }
      appliedModels.value = { ...models.value };
      emit("filter-change", models.value, isUserApply, isReset);
      if (selectedPreSavedFilter.value > 0) {
        const selectedItem = searchableFilterList.value.find(
          (item) => item.webId === selectedPreSavedFilter.value
        );
        if (
          selectedItem?.value &&
          Object.entries(JSON.parse(selectedItem.value)).toString() !==
            Object.entries(appliedModels.value).toString()
        ) {
          selectedPreSavedFilter.value = -1;
        }
      }
      const globalFilter = document.querySelector(
        "#vz-filter-global-filter-trigger"
      ) as HTMLElement;
      if (globalFilter && viewName.value === "globalFilter") {
        globalFilter.classList.remove("except-me");
      }
      triggerControl.value?.classList.remove("except-me");
    };
    const resetFilter = (filters?: Record<string, any>) => {
      if (filters) {
        models.value = { ...filters };
      } else {
        models.value = { ...defaultFilter.value };
      }
      updateFilter(false, true);
    };
    const getSavedFilterList = async () => {
      try {
        disableButton.value = true;
        savedFilterList.value =
          await SavedFilterAPI.getInstance().getSaveFilterList(viewName.value);
        searchableFilterList.value = savedFilterList.value;
        defaultFilterSaved.value = savedFilterList.value.find(
          (filter) => filter.defaultIndicator === true
        );
        if (defaultFilterSaved.value && defaultFilterSaved.value.value) {
          defaultFilterSaved.value = JSON.parse(defaultFilterSaved.value.value);
        } else {
          defaultFilterSaved.value = { ...defaultFilter.value };
        }
        await searchSavedFilters();
      } catch (e) {
        console.log(e);
      } finally {
        disableButton.value = false;
      }
    };
    const applyPreviousModels = () => {
      models.value = { ...appliedModels.value };
      if (viewName.value === "ViewRole") {
        closeToggle();
      }
      const globalFilter = document.querySelector(
        "#vz-filter-global-filter-trigger"
      ) as HTMLElement;
      if (globalFilter && viewName.value === "globalFilter") {
        globalFilter.classList.remove("except-me");
      }
      triggerControl.value?.classList.remove("except-me");
    };
    const postSavedFilter = async (req) => {
      try {
        disableButton.value = true;
        showAddEditModal.value = false;
        if (req.webId) {
          await SavedFilterAPI.getInstance().putSavedFilter({
            ...req,
            value: JSON.stringify(models.value),
          });
          toast.success(messages.updated);
        } else {
          await SavedFilterAPI.getInstance().postSavedFilter({
            title: req.title,
            value: JSON.stringify(models.value),
            frameName: viewName.value,
            publicName: false,
            editable: true,
            clientId: currentUser.value.clientId || 0,
            userId: currentUser.value.webId,
            defaultIndicator: false,
          });
          toast.success(messages.added);
        }
        const globalFilter = document.querySelector(
          "#vz-filter-global-filter-trigger"
        ) as HTMLElement;
        if (globalFilter && viewName.value === "globalFilter") {
          globalFilter.classList.remove("except-me");
        }
        triggerControl.value?.classList.remove("except-me");
        getSavedFilterList();
      } catch (e) {
        //
      } finally {
        formData.value.data = {};
        formData.value.edit = false;
        disableButton.value = false;
        showAddEditModal.value = false;
      }
    };
    const postDefaultSavedFilter = async (req) => {
      try {
        if (req.webId) {
          await SavedFilterAPI.getInstance().putSavedFilter({
            ...req,
            defaultIndicator: !req.defaultIndicator,
          });
          toast.success(messages.updated);
        }
        await getSavedFilterList();
        const isReset = false;
        if (!req.defaultIndicator && !isReset) {
          models.value = { ...defaultFilterSaved.value };
          updateFilter(true, false);
        } else {
          models.value = { ...defaultFilterSaved.value };
          updateFilter(false, true);
        }
      } catch (e) {
        //
      } finally {
        formData.value.data = {};
        formData.value.edit = false;
        disableButton.value = false;
        showAddEditModal.value = false;
        const clic = document.querySelector(".trigger-class") as HTMLElement;
        if (clic) {
          console.log("clic", clic);
          clic.click();
        }
        if (viewName.value === "ViewRole") {
          closeToggleDefault(req.webId);
        }
      }
    };
    const deletePreSavedFilter = async (item: ISavedFilter) => {
      try {
        disableButton.value = true;
        await SavedFilterAPI.getInstance().deletePreSavedFilter(item.webId);
        getSavedFilterList();
        toast.success(messages.deleted);
      } catch (e) {
        //
      } finally {
        disableButton.value = false;
        if (viewName.value === "ViewRole") {
          closeToggleDefault(item.webId);
        }
      }
    };
    const applyPreSavedFilter = (item: ISavedFilter) => {
      selectedPreSavedFilter.value = item.webId;
      const savedModels = item.value ? JSON.parse(item.value) : {};
      if (savedModels?.filterSiteIds?.length > 0) {
        savedModels.filterSiteIds = savedModels.filterSiteIds.map(
          (id) => `${id}`
        );
      }
      if (savedModels?.siteId?.length > 0) {
        savedModels.siteId = savedModels.siteId.map((id) => `${id}`);
      }
      models.value = { ...savedModels };
    };
    const editPreSavedFilter = (item: ISavedFilter) => {
      const globalFilter = document.querySelector(
        "#vz-filter-global-filter-trigger"
      ) as HTMLElement;
      if (globalFilter && viewName.value === "globalFilter") {
        globalFilter.classList.add("except-me");
      } else {
        triggerControl.value?.classList.add("except-me");
      }
      formData.value.data = item;
      formData.value.edit = true;
      showAddEditModal.value = true;
      if (viewName.value === "ViewRole") {
        closeToggleDefault(item.webId);
      }
      removeModalBackdrop();
    };
    const searchSavedFilters = () => {
      savedFilterList.value = searchableFilterList.value.filter((item) =>
        item.title
          .trim()
          .toLowerCase()
          .includes(searchTerm.value.trim().toLowerCase())
      );
      nextTick(() => {
        MenuComponent.reinitialization();
      });
    };
    const updatePreSavedFilter = () => {
      if (selectedPreSavedFilter.value > 0) {
        const selectedItem = searchableFilterList.value.find(
          (item) => item.webId === selectedPreSavedFilter.value
        );
        if (selectedItem) {
          editPreSavedFilter(selectedItem);
        }
      }
    };
    //showSavedFilters
    const handleShowHide = () => {
      showSavedFilters.value = !showSavedFilters.value;
      rowClasses.value = showSavedFilters.value
        ? "menu menu-sub menu-sub-dropdown w-sm-400px w-md-800px show"
        : "menu menu-sub menu-sub-dropdown w-300px w-md-325px show";
    };
    const resetSavedFilter = () => {
      showAddEditModal.value = false;
      formData.value.data = {};
      formData.value.edit = false;
      removeModalBackdrop();
      if (viewName.value === "ViewRole") {
        closeToggle();
      }

      // Add class excpect-me
      nextTick(() => {
        const globalFilter = document.querySelector(
          "#vz-filter-global-filter-trigger"
        ) as HTMLElement;
        if (globalFilter && viewName.value === "globalFilter") {
          const isClassExits = globalFilter.classList.contains("except-me");
          if (isClassExits) {
            globalFilter.classList.remove("except-me");
          }
          if (!isClassExits) {
            globalFilter.classList.add("except-me");
          }
        } else {
          const isClassExits =
            triggerControl.value?.classList.contains("except-me");
          if (isClassExits) {
            triggerControl.value?.classList.remove("except-me");
          }
          if (!isClassExits) {
            triggerControl.value?.classList.add("except-me");
          }
        }
      });
      // triggerControl.value?.classList.remove("except-me");
    };
    const captureVisibleEvent = (visibility: boolean) => {
      if (visibility) {
        triggerControl.value?.classList.add("except-me");
      } else {
        triggerControl.value?.classList.remove("except-me");
      }
    };
    const clickToggle = () => {
      // Always clear searchTerm when opening the filter menu
      searchTerm.value = "";

      // Set aria-expanded to false, add 'collapsed' class, and hide the search input (collapse)
      const filterId = `vz-filter-data-${viewName.value}`;
      const filterRoot = document.getElementById(filterId);
      if (filterRoot) {
        const searchBtn = filterRoot.querySelector(
          '.card-toolbar button[data-bs-toggle="collapse"]'
        );
        if (searchBtn) {
          searchBtn.setAttribute("aria-expanded", "false");
          if (!searchBtn.classList.contains("collapsed")) {
            searchBtn.classList.add("collapsed");
          }
        }
        // Hide the search input collapse
        const searchCollapse = filterRoot.querySelector("#searchMessage");
        if (searchCollapse && searchCollapse.classList.contains("show")) {
          searchCollapse.classList.remove("show");
        }
      }

      const clic = document.getElementById(
        "vz-filter-data-ViewRole"
      ) as HTMLElement;
      if (clic) {
        clic.classList.add("show");
        clic.style.cssText =
          "z-index: 2009; position: fixed; inset: 0px auto auto 0px; margin: 0px; transform: translate(646.25px, 201.25px);";
      }

      nextTick(() => {
        setTimeout(() => {
          if (scrollRef.value) {
            scrollRef.value.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }
        }, 0);
      });
    };
    const clickToggleDefaultFilter = (id) => {
      const clic = document.getElementById(
        `vz-saved-filters-${id}`
      ) as HTMLElement;
      if (clic) {
        clic.classList.add("show");
        clic.style.cssText =
          "z-index: 99999; position: fixed; inset: 0px auto auto 0px; margin: 0; transform: translate(195.25px, 195.25px);";
      }
    };
    const closeToggle = () => {
      const clic = document.getElementById(
        "vz-filter-data-ViewRole"
      ) as HTMLElement;
      if (clic.classList.contains("show")) {
        clic.classList.remove("show");
      }
    };
    const closeToggleDefault = (id) => {
      const clic = document.getElementById(
        `vz-saved-filters-${id}`
      ) as HTMLElement;
      if (clic.classList.contains("show")) {
        clic.classList.remove("show");
      }
    };

    watch(
      defaultFilterSaved,
      async (newValue) => {
        if (viewName.value === "globalFilter") {
          setTimeout(() => {
            models.value = {};
            models.value = { ...newValue };
            setActiveFilter();
          }, 1000);
        } else {
          models.value = {};
          models.value = { ...newValue };
          setActiveFilter();
        }
      },
      { deep: true }
    );

    watch(filterOptions, (newValue) => {
      // filterOptions.value = newValue;
      getLookupData();
    });
    watch(loading, (newValue) => {
      if (newValue) {
        getLookupData();
      }
    });

    return {
      clickToggle,
      closeToggle,
      models,
      items,
      activeFilter,
      updateFilter,
      resetFilter,
      getLookupData,
      lookupOptions,
      postSavedFilter,
      savedFilterList,
      showSavedFilters,
      rowClasses,
      formData,
      selectedPreSavedFilter,
      searchTerm,
      disableButton,
      deletePreSavedFilter,
      applyPreSavedFilter,
      editPreSavedFilter,
      searchSavedFilters,
      updatePreSavedFilter,
      applyPreviousModels,
      t,
      filterOptions,
      handleShowHide,
      resetSavedFilter,
      displayDate,
      displayDateTime,
      triggerControl,
      captureVisibleEvent,
      showAddEditModal,
      postDefaultSavedFilter,
      getSavedFilterList,
      handleSelect,
      clickToggleDefaultFilter,
      closeToggleDefault,
      scrollRef,
      getAppliedFilters,
    };
  },
});
